import Table from 'react-bootstrap/Table';
import {useTranslation} from "react-i18next";
import PlayerNameWithFlag from "../PlayerNameWithFlag";
import {useNavigate} from "react-router-dom";
import {Fragment, useCallback, type ReactElement} from "react";
import {Dropdown, OverlayTrigger, Popover} from "react-bootstrap";
import {getLoggedUser} from "../../../util/SecurityUtils";
import {useSetPlayerSecondRoundGroup} from "../../../hooks/mutations/useTournamentMutations";
import type {Player, PlayerScore} from "../../../types/domain";

interface PlayerScoreTableProps {
  scores: PlayerScore[];
  tournamentId?: number | string;
  displayGroups?: boolean;
  hideElo?: boolean;
  hideBuchholz?: boolean;
}

type GroupVariant = "success" | "info" | "warning" | "danger" | "light" | "dark" | "secondary";

function PlayerScoreTable(props: PlayerScoreTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleOnClick = useCallback((id: number) => navigate('/players/' + id), [navigate]);
  const DEFAULT_GROUPS = [1, 2, 3, 4, 5, 6];

  const getVariantFromGroupNumber = (groupNumber: number | null | undefined): GroupVariant => {
    switch (groupNumber) {
      case 1:
        return "success";
      case 2:
        return "info";
      case 3:
        return "warning";
      case 4:
        return "danger";
      case 5:
        return "light";
      case 6:
        return "dark";
      default:
        return "secondary";
    }
  }

  const getBuchholzPopover = () => {
    return (
      <Popover style={{maxHeight: "9rem", backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)'}}>
        <Popover.Header as="h3" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t("buchholzScore")}</Popover.Header>
        <Popover.Body style={{ color: 'var(--text-secondary)' }}>
          {t("buchholzScoreDescription")}
        </Popover.Body>
      </Popover>
    )
  }

  const setPlayerGroupMutation = useSetPlayerSecondRoundGroup();

  const handleSetPlayerGroup = useCallback(async (tournamentId: number | string, playerId: number, group: number) => {
    await setPlayerGroupMutation.mutateAsync({ tournamentId: Number(tournamentId), playerId, group });
  }, [setPlayerGroupMutation]);

  const getGroupDropdown = (player: Player, displayGroups: boolean | undefined): ReactElement | null => {
    if (!displayGroups || props.tournamentId == null) {
      return null
    }
    const tournamentId = props.tournamentId;
    return (<th onClick={(e) => e.stopPropagation()}>
      <Dropdown>
        <Dropdown.Toggle variant={getVariantFromGroupNumber(player.secondRoundGroup)} size="sm">
          {t("group") + (player.secondRoundGroup ? " " + player.secondRoundGroup : "")}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          {DEFAULT_GROUPS.map((group) => {
            return (
              <Dropdown.Item key={"group-option-" + group} onClick={() => handleSetPlayerGroup(tournamentId, player.id, group)}>
                {t("group") + " " + group}
              </Dropdown.Item>
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
    </th>
    )
  }

  const getPointsClassName = (playerScore: PlayerScore) => {
    if (playerScore.pointsWon - playerScore.pointsLost > 0) {
      return "win";
    } else if (playerScore.pointsWon - playerScore.pointsLost < 0) {
      return "loss";
    } else {
      return "tie";
    }
  }

  const getRowsFromScores = (scores: PlayerScore[], displayGroups: boolean | undefined) => {
    return scores.map((playerScore, index) => {
      const player = playerScore.player
      const isLoggedUser = getLoggedUser()?.playerId === player.id;
      const highlightedTop = isLoggedUser ? " highlighted-score-top" : "";
      const highlightedBottom = isLoggedUser ? " highlighted-score-bottom" : "";
      const highlighted = highlightedTop + highlightedBottom;
      return (
        <Fragment key={"score-fragment-" + player.id + "-" + index}>
          <tr className={"hide-on-mobile clickable" + highlighted}
              onClick={() => handleOnClick(player.id)}>
            <th>{index + 1}.</th>
            <th style={{"textAlign": "left"}}><PlayerNameWithFlag player={player} includeElo={!props.hideElo} aliasOnly={true} /></th>
            <th style={{width: "100px"}}>
              <span style={{fontWeight: "bold"}}>{playerScore.score}{t("pointAbbreviation")} </span>
              {!props.hideBuchholz && <span style={{color: "var(--text-muted)", fontSize: "small"}}> ({playerScore.buchholz})</span>}
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.matchesWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.matchesLost}</span>
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.setsWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.setsLost}</span>
            </th>
            <th style={{width: "110px"}}>
              <span className={getPointsClassName(playerScore)}>{playerScore.pointsWon > playerScore.pointsLost ? "+" : ""}{playerScore.pointsWon - playerScore.pointsLost}</span>
              <span style={{fontSize: "small"}}> (</span>
              <span className="win" style={{fontSize: "small"}}>{playerScore.pointsWon}</span>
              <span className="tie" style={{fontSize: "small"}}> - </span>
              <span className="loss" style={{fontSize: "small"}}>{playerScore.pointsLost}</span>
              <span style={{fontSize: "small"}}>)</span>
            </th>
            {getGroupDropdown(player, displayGroups)}
          </tr>
          <tr className={"hide-on-desktop clickable" + highlightedTop}
              onClick={() => handleOnClick(player.id)}>
            <th>{index + 1}.</th>
            <th style={{"textAlign": "left"}} colSpan={9}><PlayerNameWithFlag player={player} includeElo={true} aliasOnly={true} /></th>
          </tr>
          <tr className="hide-on-mobile hide-on-desktop" />
          <tr className={"hide-on-desktop clickable" + highlightedBottom}
              onClick={() => handleOnClick(player.id)}>
            <th style={{width: "100px"}}>
              <span style={{fontWeight: "bold"}}>{playerScore.score}{t("pointAbbreviation")} </span>
              {!props.hideBuchholz && <span style={{color: "var(--text-muted)", fontSize: "small"}}> ({playerScore.buchholz})</span>}
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.matchesWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.matchesLost}</span>
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.setsWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.setsLost}</span>
            </th>
            <th style={{width: "110px"}}>
              <span className={getPointsClassName(playerScore)}>{playerScore.pointsWon > playerScore.pointsLost ? "+" : ""}{playerScore.pointsWon - playerScore.pointsLost}&nbsp;</span>
              <span style={{fontSize: "x-small"}}>(</span>
              <span className="win" style={{fontSize: "x-small"}}>{playerScore.pointsWon}</span>
              <span className="tie" style={{fontSize: "x-small"}}> - </span>
              <span className="loss" style={{fontSize: "x-small"}}>{playerScore.pointsLost}</span>
              <span style={{fontSize: "x-small"}}>)</span>
            </th>
            {getGroupDropdown(player, displayGroups)}
          </tr>
          <tr className="hide-on-mobile hide-on-desktop" />
        </Fragment>
      )
    })
  }

    return (
        <Table striped bordered hover size="sm" className="text-center theme-table" style={{ backgroundColor: 'var(--table-row-bg)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
            <thead style={{"textTransform": "none"}}>
            <tr>
              <th className="hide-on-mobile">#</th>
              <th className="hide-on-mobile">{t("player")}</th>
              {props.hideBuchholz && <th style={{width: "80px"}}>{t("score")}</th> }
              {!props.hideBuchholz &&
              <th style={{width: "100px"}}><OverlayTrigger placement="bottom" overlay={getBuchholzPopover()}>
                <span>{t("score")} <span style={{textDecoration: "underline"}}>(Bch)</span></span>
              </OverlayTrigger></th>}
              <th style={{width: "80px"}}>{t("matches")}</th>
              <th style={{width: "80px"}}>{t("sets")}</th>
              <th style={{width: "110px"}}>{t("points")}</th>
              {props.displayGroups ? <th style={{width: "20px"}}>{t("group")}</th> : null}
            </tr>
            </thead>
            <tbody>
            {getRowsFromScores(props.scores, props.displayGroups)}
            </tbody>
        </Table>
    );
}

export default PlayerScoreTable
